import moment from "moment";
import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Label,
  Pagination,
  Select,
  Spinner,
  Table,
  Tabs,
  TextInput,
} from "flowbite-react";
import { LuCalendarClock } from "react-icons/lu";
import {
  HiDownload,
  HiExternalLink,
  HiOutlineAdjustments,
  HiOutlineRefresh,
  HiOutlineViewGrid,
  HiOutlineViewList,
  HiOutlineX,
  HiSearch,
} from "react-icons/hi";
import axios from "../../config/axiosInstance";
import { useQuery } from "react-query";
import { PAYMENTS } from "../../config/apiConfig";
import LastMonthsSelect from "../inputs/LastMonthsSelect";
import { formatToReal } from "../../utils/utils";
import EmptyTable from "./EmptyTable";
import { FaArrowsLeftRight } from "react-icons/fa6";

const ITEMS_PER_PAGE_OPTIONS = [10, 20, 50, 100];

const DENSITIES = {
  comfy: { row: "py-3", cell: "py-3 px-3 text-sm" },
  compact: { row: "py-1.5", cell: "py-1.5 px-2 text-xs" },
};

function StatusDot({ color = "bg-gray-300" }) {
  return <span className={`inline-block w-2 h-2 rounded-full ${color}`} />;
}

function exportCSV(rows, filename = "invoices.csv") {
  if (!rows?.length) return;
  const headers = [
    "id",
    "createdAt",
    "dueDate",
    "paidAt",
    "price",
    "type",
    "status",
    "paymentLink",
  ];
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      [
        r.id ?? "",
        r.createdAt ? moment(r.createdAt).format("YYYY-MM-DD HH:mm:ss") : "",
        r.dueDate ? moment(r.dueDate).format("YYYY-MM-DD") : "",
        r.updatedAt ? moment(r.updatedAt).format("YYYY-MM-DD HH:mm:ss") : "",
        r.price ?? "",
        r.type ?? "",
        r.status ?? (r.isPaid ? "paid" : "pending"),
        r.paymentLink ?? "",
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.setAttribute("download", filename);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const InvoicesTable = () => {
  const [period, setPeriod] = useState(moment().format("YYYY-MM"));
  const [showFilters, setShowFilters] = useState(true);

  // abas
  const [activeTab, setActiveTab] = useState("pending"); // "pending" | "paid"
  const isPaid = activeTab === "paid";

  // filtros locais
  const [methodFilter, setMethodFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [minValue, setMinValue] = useState("");
  const [maxValue, setMaxValue] = useState("");
  const [onlyOverdue, setOnlyOverdue] = useState(false); // apenas pendentes

  // densidade
  const [dense, setDense] = useState(false);

  // paginação local
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE_OPTIONS[0]);

  // ===== Query principal (lista da aba ativa) =====
  const {
    isLoading,
    error,
    data: invoices,
    refetch,
  } = useQuery(["invoices", period, isPaid], async () => {
    const { data } = await axios.get(
      `${PAYMENTS.GET_ALL}?period=${period}&isPaid=${isPaid}`
    );
    return data?.data?.data || [];
  });

  // ===== Contadores das abas =====
  const { data: pendingAll } = useQuery(
    ["invoices-count", period, "pending"],
    async () => {
      const { data } = await axios.get(
        `${PAYMENTS.GET_ALL}?period=${period}&isPaid=false`
      );
      return data?.data?.data || [];
    }
  );
  const { data: paidAll } = useQuery(
    ["invoices-count", period, "paid"],
    async () => {
      const { data } = await axios.get(
        `${PAYMENTS.GET_ALL}?period=${period}&isPaid=true`
      );
      return data?.data?.data || [];
    }
  );
  const pendingCount = pendingAll?.length || 0;
  const paidCount = paidAll?.length || 0;

  // ===== Métodos únicos (para filtro) =====
  const availableMethods = useMemo(() => {
    const base = (isPaid ? paidAll : pendingAll) || [];
    const set = new Set(base.map((i) => i?.type).filter(Boolean));
    return ["", ...Array.from(set)];
  }, [isPaid, pendingAll, paidAll]);

  // ===== Filtragem local =====
  const filtered = useMemo(() => {
    const list = invoices || [];
    const term = (searchTerm || "").trim().toLowerCase();
    const min = minValue ? parseFloat(minValue.replace(",", ".")) : null;
    const max = maxValue ? parseFloat(maxValue.replace(",", ".")) : null;

    return list.filter((inv) => {
      const byMethod = methodFilter ? inv?.type === methodFilter : true;
      const bySearch = term
        ? [inv?.id, inv?.type, inv?.paymentLink]
            .map((v) => (v ? String(v).toLowerCase() : ""))
            .some((v) => v.includes(term))
        : true;
      const price = Number(inv?.price ?? 0);
      const byMin = min !== null ? price >= min : true;
      const byMax = max !== null ? price <= max : true;
      return byMethod && bySearch && byMin && byMax;
    });
  }, [invoices, methodFilter, searchTerm, minValue, maxValue]);

  // ===== Filtro "somente atrasados" (apenas pendentes) =====
  const filteredWithOverdue = useMemo(() => {
    if (isPaid || !onlyOverdue) return filtered;
    const today = moment().startOf("day");
    return filtered.filter(
      (it) => it.dueDate && moment(it.dueDate).isBefore(today, "day")
    );
  }, [filtered, onlyOverdue, isPaid]);

  // ===== Paginação local =====
  const totalItems = filteredWithOverdue.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const currentPage = Math.min(page, totalPages);
  const paged = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredWithOverdue.slice(start, start + itemsPerPage);
  }, [filteredWithOverdue, currentPage, itemsPerPage]);

  // ===== Resumo (cards) =====
  const summary = useMemo(() => {
    const arr = filteredWithOverdue;
    const total = arr.reduce((acc, it) => acc + Number(it.price || 0), 0);
    const qty = arr.length;
    const avg = qty ? total / qty : 0;
    let vencidos = 0;
    if (!isPaid) {
      const today = moment().startOf("day");
      vencidos = arr.filter(
        (it) => it.dueDate && moment(it.dueDate).isBefore(today, "day")
      ).length;
    }
    return { total, qty, avg, vencidos };
  }, [filteredWithOverdue, isPaid]);

  // ===== Helpers =====
  const dueBadge = (dueDate) => {
    if (!dueDate) return <div>-</div>;
    const today = moment().startOf("day");
    const due = moment(dueDate).startOf("day");

    if (due.isSame(today)) {
      return (
        <Badge color="warning" className="w-fit mx-auto">
          Vence hoje
        </Badge>
      );
    }
    if (due.isBefore(today)) {
      const diff = today.diff(due, "days");
      return (
        <Badge color="failure" className="w-fit mx-auto">
          Vencido há {diff} {diff === 1 ? "dia" : "dias"}
        </Badge>
      );
    }
    const diff = due.diff(today, "days");
    return (
      <Badge color="success" className="w-fit mx-auto">
        Vence em {diff} {diff === 1 ? "dia" : "dias"}
      </Badge>
    );
  };

  const handleTabChange = (index) => {
    setActiveTab(index === 0 ? "pending" : "paid");
    setOnlyOverdue(false);
    setPage(1);
  };

  const resetFilters = () => {
    setMethodFilter("");
    setSearchTerm("");
    setMinValue("");
    setMaxValue("");
    setOnlyOverdue(false);
    setItemsPerPage(ITEMS_PER_PAGE_OPTIONS[0]);
    setPage(1);
    refetch();
  };

  const rowCls = dense ? DENSITIES.compact.row : DENSITIES.comfy.row;
  const cellCls = dense ? DENSITIES.compact.cell : DENSITIES.comfy.cell;

  return (
    <>
      {/* Header simples com título e toggle de filtros */}
      <div className="bg-[var(--bg-black)] text-white rounded-md px-4 py-3 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <LuCalendarClock className="text-xl" />
          <h2 className="text-sm md:text-base font-semibold">Faturas</h2>
          <span className="opacity-70 text-xs">
            / {moment(period, "YYYY-MM").format("MMM/YYYY")}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button color="light" onClick={() => setShowFilters((s) => !s)}>
            <HiOutlineAdjustments className="mr-2" />
            {showFilters ? "Ocultar filtros" : "Mostrar filtros"}
          </Button>
          <Button
            color="light"
            onClick={() => setDense((d) => !d)}
            title="Alternar densidade"
            className="py-1"
          >
            {dense ? (
              <HiOutlineViewList fontSize={15} />
            ) : (
              <HiOutlineViewGrid fontSize={15} />
            )}
          </Button>
        </div>
      </div>

      {/* Filtros próprios */}
      {showFilters && (
        <div className="w-full bg-white rounded-md shadow-sm p-4 mt-3 border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="flex flex-col">
              <Label className="text-xs mb-1" value="Mês" />
              <LastMonthsSelect
                value={period}
                onChange={(e) => {
                  setPeriod(e.target.value);
                  setPage(1);
                }}
                className="w-full"
              />
            </div>

            <div className="flex flex-col md:col-span-2">
              <Label
                className="text-xs mb-1"
                value="Buscar (ID, link, método…)"
              />
              <div className="flex gap-2">
                <TextInput
                  icon={HiSearch}
                  placeholder="Digite para filtrar…"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                  aria-label="Buscar faturas"
                  className="w-full"
                />
                {searchTerm && (
                  <Button
                    color="light"
                    onClick={() => setSearchTerm("")}
                    title="Limpar busca"
                  >
                    <HiOutlineX />
                  </Button>
                )}
              </div>
            </div>

            <div className="flex flex-col">
              <Label className="text-xs mb-1" value="Método" />
              <Select
                value={methodFilter}
                onChange={(e) => {
                  setMethodFilter(e.target.value);
                  setPage(1);
                }}
              >
                {availableMethods.map((m, i) => (
                  <option key={i} value={m}>
                    {m || "Todos"}
                  </option>
                ))}
              </Select>
            </div>

            <div className="flex flex-col">
              <Label className="text-xs mb-1" value="Valor mínimo" />
              <TextInput
                type="number"
                placeholder="Ex: 10"
                value={minValue}
                onChange={(e) => {
                  setMinValue(e.target.value);
                  setPage(1);
                }}
                min="0"
                step="0.01"
              />
            </div>

            <div className="flex flex-col">
              <Label className="text-xs mb-1" value="Valor máximo" />
              <TextInput
                type="number"
                placeholder="Ex: 100"
                value={maxValue}
                onChange={(e) => {
                  setMaxValue(e.target.value);
                  setPage(1);
                }}
                min="0"
                step="0.01"
              />
            </div>

            <div className="flex items-end md:justify-end md:col-span-2">
              <Button color="light" onClick={resetFilters}>
                <HiOutlineRefresh className="mr-2" />
                Limpar filtros
              </Button>
            </div>
          </div>

          {/* Pílulas rápidas (métodos) */}
          <div className="flex flex-wrap gap-2 mt-3">
            {availableMethods
              .filter((m) => m)
              .slice(0, 8)
              .map((m) => (
                <button
                  key={m}
                  onClick={() => {
                    setMethodFilter((prev) => (prev === m ? "" : m));
                    setPage(1);
                  }}
                  className={`px-2 py-1 rounded-full border text-xs ${
                    methodFilter === m
                      ? "bg-primary text-white border-primary"
                      : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
                  }`}
                >
                  {m}
                </button>
              ))}
          </div>

          {/* Chips de filtros ativos */}
          {(methodFilter ||
            minValue ||
            maxValue ||
            searchTerm ||
            onlyOverdue) && (
            <div className="flex flex-wrap gap-2 pt-3">
              {methodFilter && (
                <span className="inline-flex items-center gap-1 bg-gray-100 border border-gray-200 text-gray-700 text-xs px-2 py-1 rounded-full">
                  Método: {methodFilter}
                  <button
                    onClick={() => setMethodFilter("")}
                    className="opacity-70 hover:opacity-100"
                  >
                    <HiOutlineX />
                  </button>
                </span>
              )}
              {minValue && (
                <span className="inline-flex items-center gap-1 bg-gray-100 border border-gray-200 text-gray-700 text-xs px-2 py-1 rounded-full">
                  Min: {minValue}
                  <button
                    onClick={() => setMinValue("")}
                    className="opacity-70 hover:opacity-100"
                  >
                    <HiOutlineX />
                  </button>
                </span>
              )}
              {maxValue && (
                <span className="inline-flex items-center gap-1 bg-gray-100 border border-gray-200 text-gray-700 text-xs px-2 py-1 rounded-full">
                  Máx: {maxValue}
                  <button
                    onClick={() => setMaxValue("")}
                    className="opacity-70 hover:opacity-100"
                  >
                    <HiOutlineX />
                  </button>
                </span>
              )}
              {searchTerm && (
                <span className="inline-flex items-center gap-1 bg-gray-100 border border-gray-200 text-gray-700 text-xs px-2 py-1 rounded-full">
                  Busca: “{searchTerm}”
                  <button
                    onClick={() => setSearchTerm("")}
                    className="opacity-70 hover:opacity-100"
                  >
                    <HiOutlineX />
                  </button>
                </span>
              )}
              {!isPaid && onlyOverdue && (
                <span className="inline-flex items-center gap-1 bg-gray-100 border border-gray-200 text-gray-700 text-xs px-2 py-1 rounded-full">
                  Somente atrasados
                  <button
                    onClick={() => setOnlyOverdue(false)}
                    className="opacity-70 hover:opacity-100"
                  >
                    <HiOutlineX />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Cards de resumo */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <div className="text-xs text-gray-500">Total (filtrado)</div>
          <div className="text-xl font-semibold">
            {formatToReal(summary.total)}
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <div className="text-xs text-gray-500">Quantidade</div>
          <div className="text-xl font-semibold">{summary.qty}</div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <div className="text-xs text-gray-500">Ticket médio</div>
          <div className="text-xl font-semibold">
            {formatToReal(summary.avg)}
          </div>
        </div>
        {!isPaid && (
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="text-xs text-gray-500">Vencidos</div>
            <div className="text-xl font-semibold">{summary.vencidos}</div>
          </div>
        )}
      </div>

      {/* Ações topo (mobile tip) */}
      <div className="flex items-center justify-between mt-4">
        <small
          className="gap-2 items-center my-1 flex md:hidden justify-center"
          style={{ fontSize: 10 }}
        >
          <span>Deslize para o lado para visualizar todas as informações</span>{" "}
          <FaArrowsLeftRight />
        </small>
      </div>

      {/* Tabs */}
      <div className="bg-white p-4 mt-3 shadow-md rounded-md">
        <Tabs
          aria-label="Faturas"
          variant="underline"
          onActiveTabChange={handleTabChange}
        >
          {/* PENDENTES */}
          <Tabs.Item
            active={activeTab === "pending"}
            title={`Pendentes (${pendingCount})`}
          >
            {error ? (
              <Alert color="failure" className="mt-2">
                <span className="font-medium">Erro:</span> Não foi possível
                carregar os dados.
                <Button
                  size="xs"
                  color="failure"
                  className="ml-2"
                  onClick={() => refetch()}
                >
                  Tentar novamente
                </Button>
              </Alert>
            ) : isLoading ? (
              <div className="mt-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3 py-2">
                    <Spinner size="sm" />
                    <div className="h-3 bg-gray-200 rounded-full w-1/3" />
                    <div className="h-3 bg-gray-200 rounded-full w-1/4" />
                    <div className="h-3 bg-gray-200 rounded-full w-1/6" />
                  </div>
                ))}
              </div>
            ) : filteredWithOverdue.length > 0 ? (
              <>
                <div className="overflow-x-auto rounded-lg mt-2 border border-gray-200">
                  <Table className="min-w-[720px]">
                    <Table.Head className="sticky top-0 z-10">
                      <Table.HeadCell className="bg-gray-100 text-center">
                        Criação
                      </Table.HeadCell>
                      <Table.HeadCell className="bg-gray-100 text-center">
                        Vencimento
                      </Table.HeadCell>
                      <Table.HeadCell className="bg-gray-100 text-right">
                        Valor
                      </Table.HeadCell>
                      <Table.HeadCell className="bg-gray-100 text-center">
                        Método
                      </Table.HeadCell>
                      <Table.HeadCell className="bg-gray-100 text-center">
                        Status
                      </Table.HeadCell>
                      <Table.HeadCell className="bg-gray-100 text-center">
                        Ações
                      </Table.HeadCell>
                    </Table.Head>
                    <Table.Body className="divide-y">
                      {paged.map((invoice, index) => {
                        const isOverdue =
                          invoice.dueDate &&
                          moment(invoice.dueDate)
                            .startOf("day")
                            .isBefore(moment().startOf("day"));
                        return (
                          <Table.Row
                            key={invoice.id ?? index}
                            className={`hover:bg-gray-50 ${rowCls}`}
                          >
                            <Table.Cell className={`text-center ${cellCls}`}>
                              {invoice.createdAt
                                ? moment(invoice.createdAt).format("DD/MM/YYYY")
                                : "-"}
                            </Table.Cell>
                            <Table.Cell className={`text-center ${cellCls}`}>
                              {dueBadge(invoice.dueDate)}
                            </Table.Cell>
                            <Table.Cell
                              className={`text-right font-medium ${cellCls}`}
                            >
                              {formatToReal(invoice.price)}
                            </Table.Cell>
                            <Table.Cell className={`text-center ${cellCls}`}>
                              {invoice.type || "-"}
                            </Table.Cell>
                            <Table.Cell className={`text-center ${cellCls}`}>
                              <span className="inline-flex items-center gap-2">
                                <StatusDot
                                  color={
                                    isOverdue ? "bg-red-500" : "bg-amber-400"
                                  }
                                />
                                {isOverdue ? "Atrasado" : "Aberto"}
                              </span>
                            </Table.Cell>
                            <Table.Cell className={`text-center ${cellCls}`}>
                              {invoice.paymentLink ? (
                                <a
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded border text-xs hover:bg-gray-50"
                                  href={invoice.paymentLink}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <HiExternalLink />
                                  Pagar
                                </a>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </Table.Cell>
                          </Table.Row>
                        );
                      })}
                    </Table.Body>
                  </Table>
                </div>

                {/* Paginação */}
                <div className="flex items-center justify-center mt-3">
                  <div className="text-xs text-gray-500">
                    Mostrando {(currentPage - 1) * itemsPerPage + 1}–
                    {Math.min(currentPage * itemsPerPage, totalItems)} de{" "}
                    {totalItems}
                  </div>
                </div>
              </>
            ) : (
              <EmptyTable icon="search_off">
                Nenhum pagamento pendente.
              </EmptyTable>
            )}
          </Tabs.Item>

          {/* PAGOS */}
          <Tabs.Item
            active={activeTab === "paid"}
            title={`Pagos (${paidCount})`}
          >
            {error ? (
              <Alert color="failure" className="mt-2">
                <span className="font-medium">Erro:</span> Não foi possível
                carregar os dados.
                <Button
                  size="xs"
                  color="failure"
                  className="ml-2"
                  onClick={() => refetch()}
                >
                  Tentar novamente
                </Button>
              </Alert>
            ) : isLoading ? (
              <div className="mt-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3 py-2">
                    <Spinner size="sm" />
                    <div className="h-3 bg-gray-200 rounded-full w-1/3" />
                    <div className="h-3 bg-gray-200 rounded-full w-1/4" />
                    <div className="h-3 bg-gray-200 rounded-full w-1/6" />
                  </div>
                ))}
              </div>
            ) : filteredWithOverdue.length > 0 ? (
              <>
                <div className="overflow-x-auto rounded-lg mt-2 border border-gray-200">
                  <Table className="min-w-[720px]">
                    <Table.Head className="sticky top-0 z-10">
                      <Table.HeadCell className="bg-gray-100 text-center">
                        Criação
                      </Table.HeadCell>
                      <Table.HeadCell className="bg-gray-100 text-center">
                        Pago em
                      </Table.HeadCell>
                      <Table.HeadCell className="bg-gray-100 text-right">
                        Valor
                      </Table.HeadCell>
                      <Table.HeadCell className="bg-gray-100 text-center">
                        Método
                      </Table.HeadCell>
                    </Table.Head>
                    <Table.Body className="divide-y">
                      {paged.map((invoice, index) => (
                        <Table.Row
                          key={invoice.id ?? index}
                          className={`hover:bg-gray-50 ${rowCls}`}
                        >
                          <Table.Cell className={`text-center ${cellCls}`}>
                            {invoice.createdAt
                              ? moment(invoice.createdAt).format("DD/MM/YYYY")
                              : "-"}
                          </Table.Cell>
                          <Table.Cell className={`text-center ${cellCls}`}>
                            {invoice.updatedAt
                              ? moment(invoice.updatedAt).format("DD/MM/YYYY")
                              : "-"}
                          </Table.Cell>
                          <Table.Cell
                            className={`text-right font-medium ${cellCls}`}
                          >
                            {formatToReal(invoice.price)}
                          </Table.Cell>
                          <Table.Cell className={`text-center ${cellCls}`}>
                            {invoice.type || "-"}
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                </div>

                {/* Paginação */}
                <div className="flex items-center justify-between mt-3">
                  <div className="text-xs text-gray-500">
                    Mostrando {(currentPage - 1) * itemsPerPage + 1}–
                    {Math.min(currentPage * itemsPerPage, totalItems)} de{" "}
                    {totalItems}
                  </div>
                  <Pagination
                    currentPage={currentPage}
                    onPageChange={setPage}
                    totalPages={totalPages}
                  />
                </div>
              </>
            ) : (
              <EmptyTable icon="search_off">
                Nenhum pagamento encontrado.
              </EmptyTable>
            )}
          </Tabs.Item>
        </Tabs>
      </div>
    </>
  );
};

export default InvoicesTable;
